import React, { useCallback, useEffect, useRef } from "react";
import ConfirmDialog from "./ConfirmDialog";
import Button from "../../ui/Button";
import { useDestructiveAction } from "../../destructiveActions";
import { useToast } from "./ToastProvider";

const isProd = process.env.NODE_ENV === "production";

const resolveTypedValue = (entry, dynamicValue) => {
  if (!entry || !entry.requires_typed_confirmation) return null;
  const hint = entry.typed_confirmation_value_hint;
  if (!hint) return null;
  if (entry.typed_confirmation_mode === "static") {
    return String(hint);
  }
  if (entry.typed_confirmation_mode === "static_plus_dynamic") {
    if (!dynamicValue) return null;
    const hintText = String(hint);
    const start = hintText.indexOf("<");
    const end = hintText.indexOf(">", start + 1);
    if (start !== -1 && end !== -1) {
      return `${hintText.slice(0, start)}${dynamicValue}${hintText.slice(end + 1)}`;
    }
    return `${hintText} ${dynamicValue}`.trim();
  }
  return null;
};

const buildMessageLines = ({
  action,
  resourceDisplayName,
  context,
  irreversibleNote,
  complianceNotice,
}) => {
  const lines = [];
  if (action?.description) {
    lines.push(action.description);
  } else {
    lines.push("This action is destructive.");
  }
  if (resourceDisplayName) {
    lines.push(`Target: ${resourceDisplayName}`);
  }
  if (typeof context?.count === "number") {
    lines.push(`Items affected: ${context.count}`);
  }
  if (context?.scopeLabel) {
    lines.push(`Scope: ${context.scopeLabel}`);
  }
  if (context?.cascadeSummary) {
    lines.push(`Also affects: ${context.cascadeSummary}`);
  }
  if (irreversibleNote) {
    lines.push(irreversibleNote);
  }
  if (complianceNotice) {
    lines.push("Compliance notice: This action is audited and may be irreversible.");
  }
  return lines;
};

export default function DestructiveConfirm({
  actionId,
  open,
  onCancel,
  onConfirm,
  resourceDisplayName,
  dynamicTypedValue,
  context,
  variantOverride,
  backdropStyle,
  containerStyle,
  ...rest
}) {
  const toast = useToast();
  const { action, error } = useDestructiveAction(actionId);
  const wasOpen = useRef(false);

  const emitTelemetry = useCallback((event) => {
    if (typeof window === "undefined") return;
    try {
      window.dispatchEvent(
        new CustomEvent("destructive-action", {
          detail: { action_id: actionId, event },
        })
      );
    } catch (_err) {
      // no-op
    }
  }, [actionId]);

  const handleCancel = () => {
    emitTelemetry("cancel");
    onCancel?.();
  };

  useEffect(() => {
    if (open && !wasOpen.current) {
      emitTelemetry("open");
    }
    wasOpen.current = Boolean(open);
  }, [open, emitTelemetry]);

  if (!open) return null;

  if (!isProd) {
    const forbiddenProps = [
      "title",
      "message",
      "typedConfirmationText",
      "typedConfirmationLabel",
      "confirmLabel",
      "confirmVariant",
      "cancelLabel",
      "cancelVariant",
    ];
    const usedForbidden = forbiddenProps.filter((key) => Object.prototype.hasOwnProperty.call(rest, key));
    if (usedForbidden.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(
        "DestructiveConfirm received forbidden props. Use registry/context instead.",
        usedForbidden
    );
    }
  }

  if ((error || !action) && isProd) {
    // eslint-disable-next-line no-console
    console.warn(`DestructiveConfirm fallback for ${actionId}`, error || "missing registry entry");
  }

  if (error && !isProd) {
    return (
      <ConfirmDialog
        open={open}
        message={`Registry lookup failed for ${actionId}.`}
        confirmDisabled
        onCancel={handleCancel}
        onConfirm={onConfirm}
        backdropStyle={backdropStyle}
        containerStyle={containerStyle}
      />
    );
  }

  if (!action && !error && !isProd) {
    return (
      <ConfirmDialog
        open={open}
        message={`Missing registry entry for ${actionId}.`}
        confirmDisabled
        onCancel={handleCancel}
        onConfirm={onConfirm}
        backdropStyle={backdropStyle}
        containerStyle={containerStyle}
      />
    );
  }

  if (!action || error) {
    return (
      <ConfirmDialog
        open={open}
        message="This action is destructive and cannot be undone."
        onCancel={handleCancel}
        onConfirm={onConfirm}
        backdropStyle={backdropStyle}
        containerStyle={containerStyle}
      />
    );
  }

  const tier = action.tier;
  const requiresTyped = Boolean(action.requires_typed_confirmation);
  const typedValue = resolveTypedValue(action, dynamicTypedValue);
  const needsDynamic =
    requiresTyped && action.typed_confirmation_mode === "static_plus_dynamic" && !dynamicTypedValue;
  const typedError = needsDynamic
    ? "This action requires a specific identifier before it can be confirmed."
    : null;

  const complianceNotice =
    action.scope === "tenant" || action.scope === "global" || tier === "catastrophic";
  const irreversibleNote =
    context?.irreversibleNote || (tier === "high" || tier === "catastrophic"
      ? "This action is irreversible."
      : null);

  const messageLines = buildMessageLines({
    action,
    resourceDisplayName,
    context,
    irreversibleNote,
    complianceNotice,
  });

  const messageNode = (
    <div>
      {messageLines.map((line, idx) => (
        <p key={`${actionId}-msg-${idx}`} style={{ margin: idx === 0 ? 0 : "6px 0 0" }}>
          {line}
        </p>
      ))}
      {typedError && (
        <p style={{ marginTop: 8, color: "#b91c1c", fontSize: 12 }}>{typedError}</p>
      )}
    </div>
  );

  const forceModal = variantOverride === "modal";

  let pattern = "modal";
  if (!requiresTyped && tier === "low" && !forceModal) {
    pattern = "inline";
  }

  if (tier === "high" || tier === "catastrophic" || forceModal) {
    pattern = "modal";
  }

  const confirmVariant = tier === "high" || tier === "catastrophic" ? "destructive" : "destructive";
  const cancelVariant = tier === "high" || tier === "catastrophic" ? "primary" : "secondary";
  const confirmDisabled = Boolean(typedError);

  const handleConfirm = async (confirmationText) => {
    const result = await onConfirm?.(confirmationText);
    emitTelemetry("confirm");
    if (action?.undo_supported && typeof result === "function") {
      const undoLabel = "Undo";
      const message = resourceDisplayName ? `${resourceDisplayName} removed.` : "Action completed.";
      const duration = action?.undo_window_seconds ? action.undo_window_seconds * 1000 : undefined;
      toast?.showToast?.(message, { type: "success", actionLabel: undoLabel, onAction: result, duration });
    }
  };

  if (pattern === "inline") {
    return (
      <div
        data-testid="destructive-inline"
        style={{
          marginTop: "6px",
          padding: "8px 10px",
          borderRadius: 6,
          border: "1px solid #e5e7eb",
          background: "#f9fafb",
        }}
      >
        {messageNode}
        <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant={confirmVariant} onClick={() => handleConfirm()} disabled={confirmDisabled}>
            Confirm
          </Button>
        </div>
      </div>
    );
  }

  return (
    <ConfirmDialog
      open={open}
      message={messageNode}
      typedConfirmationText={typedValue}
      typedConfirmationLabel="Type to confirm"
      confirmVariant={confirmVariant}
      cancelVariant={cancelVariant}
      confirmDisabled={confirmDisabled}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      backdropStyle={backdropStyle}
      containerStyle={containerStyle}
    />
  );
}
